import { inject, Injectable } from '@angular/core';

import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { BormaDagoErrorCode } from '@ts/shared/api/util-borma-dago';
import {
  AuthenticationAudienceToken,
  AuthenticationJwtState,
} from '@ts/shared/authentication/util-core';
import { logger } from '@ts/shared/logging/util-core';

export const COOKIE_IS_AUTHENTICATED = 'AUTHENTICATION_TOKEN_EXISTS';

/**
 * Leaf service that owns the JWT authentication state and the
 * COOKIE_IS_AUTHENTICATED marker.  Has no dependency on
 * AuthenticationService or any API consumer, so it can be imported freely
 * by downstream services without creating a DI cycle.
 */
@Injectable({ providedIn: 'root' })
export class AuthJwtTokenStore {
  private cookieService = inject(CookieService);

  /** Constant. Public so API consumers can read it without importing AuthenticationService. */
  readonly TOKEN_NOT_VALID_ERROR_CODE: BormaDagoErrorCode = 'token_not_valid';

  private readonly stateSubject =
    new BehaviorSubject<AuthenticationJwtState | null>(null);

  readonly state$: Observable<AuthenticationJwtState | null> =
    this.stateSubject.asObservable();

  readonly isLoggedIn$: Observable<boolean> = this.state$.pipe(map((s) => !!s));

  private readonly audienceTokens = new Map<
    string,
    AuthenticationAudienceToken
  >();

  private clearCount = 0;

  /** Synchronous accessor used in AuthenticationService.logout() to decide
   *  whether to show the logged-out toast. */
  get currentState(): AuthenticationJwtState | null {
    return this.stateSubject.value;
  }

  /**
   * Set the new JWT state.
   *
   * The COOKIE_IS_AUTHENTICATED marker is deliberately not written here: the
   * server sets it alongside the credential on every login and renewal, which
   * is what keeps Safari from wiping it after 7 days (#10905).
   */
  setState(state: AuthenticationJwtState): void {
    this.stateSubject.next(state);
    logger.info('JWT state updated successfully');
  }

  /**
   * The token held for another service, or null when none is held.
   */
  audienceToken(audience: string): AuthenticationAudienceToken | null {
    return this.audienceTokens.get(audience) ?? null;
  }

  setAudienceToken(audience: string, token: AuthenticationAudienceToken): void {
    this.audienceTokens.set(audience, token);
  }

  /**
   * Advances on every {@link clearState}, so a renewal started before a logout
   * can tell that its token belongs to a session that has ended.
   */
  get sessionGeneration(): number {
    return this.clearCount;
  }

  /**
   * Clear the JWT state and every audience token, and delete the legacy
   * COOKIE_IS_AUTHENTICATED marker.
   *
   * The server clears its own parent-domain marker on logout. This delete only
   * reaches the host-only copy written by earlier frontend builds, so it stays
   * until those have expired for everyone (#10905).
   */
  clearState(): void {
    this.stateSubject.next(null);
    this.audienceTokens.clear();
    this.clearCount += 1;
    this.cookieService.delete(
      COOKIE_IS_AUTHENTICATED,
      /* path = */ '/',
      /* domain = */ undefined,
      /* secure = */ true,
      /* sameSite = */ 'None',
    );
  }
}
